/**
 * Draggable Column Item
 *
 * Reusable row component for column visibility menus that can be:
 * - Toggled on/off via checkbox
 * - Dragged to reorder (when visible)
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars2Icon } from '@heroicons/react/24/outline';

import { cn } from '@/lib/utils';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface DraggableColumnItemProps {
  /** Unique identifier for the column */
  id: string;
  /** Display label for the column */
  label: string;
  /** Whether the column is currently visible */
  isVisible: boolean;
  /** Callback when the column is toggled */
  onToggle: () => void;
  /** Whether drag-drop is enabled (only for visible columns) */
  isDraggable: boolean;
  /** Whether the checkbox is disabled (e.g., last visible column) */
  disabled?: boolean;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export const DraggableColumnItem = ({
  id,
  label,
  isVisible,
  onToggle,
  isDraggable,
  disabled = false,
}: DraggableColumnItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const inputId = `column-visibility-${id}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-1.5 rounded px-1.5 py-1',
        'hover:bg-zinc-100 dark:hover:bg-zinc-700/50',
        isDragging && 'bg-zinc-100 shadow-sm dark:bg-zinc-700',
      )}
      {...attributes}
    >
      {/* Drag Handle - only for visible/draggable columns */}
      {isDraggable ? (
        <div
          {...listeners}
          className={cn(
            'flex h-4 w-4 shrink-0 cursor-grab items-center justify-center rounded touch-none',
            'text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300',
            'active:cursor-grabbing',
            isDragging && 'cursor-grabbing',
          )}
        >
          <Bars2Icon className="h-3 w-3" />
        </div>
      ) : (
        // Spacer for alignment when not draggable
        <div className="h-4 w-4 shrink-0" />
      )}

      {/* Checkbox */}
      <label
        htmlFor={inputId}
        className={cn(
          'flex flex-1 items-center gap-1.5',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          id={inputId}
          type="checkbox"
          checked={isVisible}
          onChange={onToggle}
          disabled={disabled}
          className={cn(
            'h-3.5 w-3.5 shrink-0 rounded border-zinc-300',
            'text-blue-600 focus:ring-1 focus:ring-blue-500 focus:ring-offset-0',
            'dark:border-zinc-600 dark:bg-zinc-700 dark:checked:bg-blue-600',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        />
        <span
          className={cn(
            'truncate text-[11px]',
            isVisible
              ? 'font-medium text-zinc-900 dark:text-white'
              : 'text-zinc-600 dark:text-zinc-400',
            disabled && 'opacity-50',
          )}
        >
          {label}
        </span>
      </label>
    </div>
  );
};
